import { Arrow, Figure, INK_FAINT, INK_MUTED, LINE_FAINT, MONO, Lane, Node, Note } from './primitives';

/**
 * One run end to end, in the shape the real traces take.
 *
 * A waterfall rather than a flow chart, because a trace's subject is *order* — what was called, what
 * came back, what that changed. The two things a reader cannot get from the text version are drawn
 * rather than written: the fan-out at the destinations (one run, three trees, three different tool
 * chains) and the confidence each destination was chosen at, which is the number the whole placement
 * phase exists to produce.
 *
 * The content is the illustration from the document, unchanged — this is the same trace, laid out.
 */

/** Tool colour by what the call is *for*, matching the three groups the roster is built in. */
const READ = INK_MUTED;
const DECIDE = 'var(--color-warning-text)';
const CHECK = 'var(--color-info-text)';
const SETTLE = 'var(--color-success-text)';

interface Step {
  tool: string;
  arg?: string;
  result?: string[];
  tone: string;
}

const PHASE_ONE: Step[] = [
  { tool: 'ingest_projects', result: ['9 projects, 2 with no description'], tone: READ },
  { tool: 'ingest_outline', arg: 'Thrasos', result: ['31 pages'], tone: READ },
  { tool: 'ingest_outline', arg: 'Heist', result: ['12 pages'], tone: READ },
  { tool: 'ingest_search', arg: '"release variant"', result: ['4 hits, 3 in Heist'], tone: READ },
  { tool: 'ingest_read', arg: 'Heist:product/release-variants', tone: READ },
  {
    tool: 'ingest_assign',
    arg: 'item 2 → Thrasos   "Goal: scale the portfolio…"',
    result: ['placed, 3,902 chars'],
    tone: DECIDE,
  },
  {
    tool: 'ingest_assign',
    arg: 'item 2 → Heist   "## Heist release variant…"',
    result: ['placed, 1,431 chars · sharedWith: none'],
    tone: DECIDE,
  },
  { tool: 'ingest_discard', arg: 'item 3   "scheduling chatter"', result: ['212 chars'], tone: DECIDE },
  {
    tool: 'ingest_review_plan',
    result: [
      'Thrasos 2 parts 7,740 · Heist 1 part 1,431',
      'coverage 100 / 97 / 94 / 100% · unclaimed 1 part, 168 chars',
    ],
    tone: CHECK,
  },
  {
    tool: 'ingest_confirm',
    result: ['“Two engagements in one channel; the Thrasos spans', 'stop before the Heist section.”'],
    tone: SETTLE,
  },
];

/** One trace line: the call on the left, its argument beside it, what came back on the right. */
function TraceRow({ y, step }: { y: number; step: Step }) {
  const lines = Math.max(1, step.result?.length ?? 1);
  return (
    <g>
      <text x={16} y={y} fontSize={9.5} fontFamily={MONO} fill={step.tone} fontWeight={600}>
        {step.tool}
      </text>
      {step.arg && (
        <text x={168} y={y} fontSize={9.5} fill="var(--color-ink-secondary)">
          {step.arg}
        </text>
      )}
      {step.result?.map((line, i) => (
        <text key={i} x={434} y={y + i * 11} fontSize={9.5} fill={INK_MUTED}>
          {line}
        </text>
      ))}
      <line x1={16} y1={y + 5 + (lines - 1) * 11} x2={744} y2={y + 5 + (lines - 1) * 11} stroke={LINE_FAINT} strokeWidth={1} />
    </g>
  );
}

/** A destination as the placement phase settled it: the project, and how sure it was. */
function Destination({
  x,
  name,
  confidence,
  fellBack = false,
}: {
  x: number;
  name: string;
  confidence: number;
  fellBack?: boolean;
}) {
  const w = 240;
  const tone = fellBack ? 'var(--color-warning-text)' : 'var(--color-ink)';
  const bar = fellBack ? 'var(--color-warning-ring)' : 'var(--color-success-ring)';
  return (
    <g>
      <rect
        x={x}
        y={0}
        width={w}
        height={46}
        rx={6}
        fill="var(--color-card)"
        stroke={fellBack ? 'var(--color-warning-ring)' : 'var(--color-border-strong)'}
        strokeWidth={1}
        {...(fellBack ? { strokeDasharray: '4 3' } : {})}
      />
      <text x={x + 12} y={19} fontSize={11.5} fontWeight={600} fill={tone}>
        {name}
      </text>
      <text x={x + w - 12} y={19} textAnchor="end" fontSize={10} fontFamily={MONO} fill={INK_MUTED}>
        {confidence.toFixed(2)}
      </text>
      <rect x={x + 12} y={28} width={w - 24} height={5} rx={2.5} fill="var(--color-surface-muted)" />
      <rect x={x + 12} y={28} width={Math.max(2, (w - 24) * confidence)} height={5} rx={2.5} fill={bar} />
      {fellBack && (
        <text x={x + 12} y={43} fontSize={8.5} fill="var(--color-warning-text)">
          fellBack — nobody claimed it
        </text>
      )}
    </g>
  );
}

/** One destination's write loop: the chain it ran, and what it committed. */
function WriteChain({
  x,
  y,
  title,
  chain,
  outcome,
  flagged = false,
}: {
  x: number;
  y: number;
  title: string;
  chain: string[];
  outcome: string;
  flagged?: boolean;
}) {
  const w = 240;
  return (
    <g>
      <text x={x + 12} y={y + 14} fontSize={10.5} fontWeight={600} fill="var(--color-ink)">
        {title}
      </text>
      <text x={x + w - 12} y={y + 14} textAnchor="end" fontSize={9} fill={INK_FAINT}>
        pass 1/1
      </text>
      {chain.map((tool, i) => (
        <g key={tool}>
          <rect
            x={x + 12}
            y={y + 24 + i * 21}
            width={w - 24}
            height={17}
            rx={4}
            fill="var(--color-surface-muted)"
            stroke="var(--color-border)"
            strokeWidth={1}
          />
          <text x={x + 20} y={y + 36 + i * 21} fontSize={9} fontFamily={MONO} fill="var(--color-ink-secondary)">
            {tool}
          </text>
        </g>
      ))}
      <rect
        x={x + 12}
        y={y + 30 + chain.length * 21}
        width={w - 24}
        height={20}
        rx={4}
        fill={flagged ? 'var(--color-warning-bg)' : 'var(--color-success-bg)'}
        stroke={flagged ? 'var(--color-warning-ring)' : 'var(--color-success-ring)'}
        strokeWidth={1}
      />
      <text
        x={x + w / 2}
        y={y + 43 + chain.length * 21}
        textAnchor="middle"
        fontSize={9}
        fontWeight={600}
        fill={flagged ? 'var(--color-warning-text)' : 'var(--color-success-text)'}
      >
        {outcome}
      </text>
    </g>
  );
}

export function WorkedRun() {
  // Row positions, accumulated so a multi-line result pushes everything below it down.
  let y = 0;
  const rows = PHASE_ONE.map((step) => {
    const at = y;
    y += 16 + Math.max(0, (step.result?.length ?? 1) - 1) * 11;
    return { at, step };
  });
  const phaseOneTop = 132;
  // The 54 is the lane label plus the column headings, which rows are offset by; the tail is the
  // clearance under the last row's divider. Derived rather than guessed, so a longer result pushes
  // the lane rather than spilling out of it.
  const phaseOneHeight = 54 + y + 10;
  const destTop = phaseOneTop + phaseOneHeight + 34;
  const phaseTwoTop = destTop + 88;
  const phaseTwoHeight = 208;
  const height = phaseTwoTop + phaseTwoHeight + 66;

  return (
    <Figure
      title="A worked example: one batched run placed into three projects"
      width={760}
      height={height}
      caption="Ten placement calls settle three destinations, and each destination's write loop then sees only the spans assigned to it — which is what stops each project's agent writing up the others' material. Only Heist's create falls under its 0.80 floor, so only it is flagged."
    >
      {/* ── Before the run ────────────────────────────────────────── */}
      <Node x={0} y={16} w={238} h={44} title="00:00 Pacific" sub="the root's window fires" tone="info" />
      <Arrow points={[[238, 38], [261, 38]]} />
      <Node x={261} y={16} w={238} h={44} title="KbRun QUEUED" sub="triage = true" tone="default" />
      <Arrow points={[[499, 38], [522, 38]]} />
      <Node x={522} y={16} w={238} h={44} title="admission" sub="nothing ahead · lease taken" tone="default" />

      <Note
        x={0}
        y={80}
        lines={[
          'groupByBudget → 1 batch · 4 items · 18,412 of 24,000 characters',
          'label “slack:C07PROD, Partner sync +1 (4 items · SLACK, GRANOLA)” — the channel id resolves to #product-eng when the row is drawn',
        ]}
      />

      {/* ── Phase one ─────────────────────────────────────────────── */}
      <Lane x={0} y={phaseOneTop} w={760} h={phaseOneHeight} label="phase one · placement, read-only" tone="accent" />
      <Note x={16} y={phaseOneTop + 34} lines="TOOL" size={8} weight={700} color={INK_FAINT} />
      <Note x={168} y={phaseOneTop + 34} lines="ARGUMENT" size={8} weight={700} color={INK_FAINT} />
      <Note x={434} y={phaseOneTop + 34} lines="RESULT" size={8} weight={700} color={INK_FAINT} />
      {rows.map(({ at, step }, i) => (
        <TraceRow key={`${step.tool}-${i}`} y={phaseOneTop + 54 + at} step={step} />
      ))}

      {/* ── What it settled on ────────────────────────────────────── */}
      <Note x={0} y={destTop - 14} lines="DESTINATIONS" size={8} weight={700} color={INK_FAINT} />
      <g transform={`translate(0 ${destTop})`}>
        <Destination x={0} name="Thrasos" confidence={0.91} />
        <Destination x={260} name="Heist" confidence={0.86} />
        <Destination x={520} name="Organization" confidence={0} fellBack />
      </g>
      <Note
        x={0}
        y={destTop + 68}
        lines="reserveDestinations → 3 leases taken before the first write; the root's is the one the run already holds"
      />

      {/* ── Phase two ─────────────────────────────────────────────── */}
      <Lane
        x={0}
        y={phaseTwoTop}
        w={760}
        h={phaseTwoHeight}
        label="phase two · one destination at a time, each seeing only its own parts"
        tone="accent"
      />
      <WriteChain
        x={0}
        y={phaseTwoTop + 24}
        title="Thrasos"
        chain={['kb_outline', 'kb_search', 'kb_read', 'kb_stage_update ×2', 'kb_review_plan', 'kb_commit']}
        outcome="2 amended · 6 citations"
      />
      <WriteChain
        x={260}
        y={phaseTwoTop + 24}
        title="Heist"
        chain={['kb_search', 'kb_stage_create', 'kb_commit']}
        outcome="1 created at 0.78 → PENDING"
        flagged
      />
      <WriteChain
        x={520}
        y={phaseTwoTop + 24}
        title="Organization"
        chain={['kb_stage_update', 'kb_commit']}
        outcome="1 amended · 1 citation"
      />

      {/* ── Outcome ───────────────────────────────────────────────── */}
      <Node
        x={0}
        y={phaseTwoTop + phaseTwoHeight + 16}
        w={760}
        h={40}
        title="run COMPLETED · 1 created · 3 amended · 1 flagged"
        sub="segments INGESTED · descriptions refreshed for Thrasos and Heist"
        tone="success"
      />
    </Figure>
  );
}
